import React, { useCallback } from "react";
import { View, ScrollView } from "react-native";
import { usePagination } from "@/hooks/usePagination";
import { useRouter, useLocalSearchParams } from "expo-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useI18n } from "@/context/I18nContext";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { SearchInput } from "@/components/ui/SearchInput";
import { Badge } from "@/components/ui/Badge";
import { StatCard } from "@/components/ui/StatCard";
import { Table, type TableColumn } from "@/components/ui/Table";
import { TabBarNavigation } from "@/components/ui/TabBarNavigation";
import { Icon } from "@/components/ui/Icon";
import { Feather } from "@expo/vector-icons";
import { reviewApi, type ApplicationSummary } from "@/lib/api/review";
import { PageFooter } from "@/components/ui/PageFooter";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { formatRelativeTime, formatSplitDateTime } from "@/lib/dateFormat";

export default function ApplicationListScreen() {
  const { t, locale } = useI18n();
  const router = useRouter();
  const { top } = useSafeAreaInsets();

  const { tab: tabParam } = useLocalSearchParams<{
    tab?: "all" | "pending_review" | "approved" | "rejected";
  }>();
  const activeTab: "all" | "pending_review" | "approved" | "rejected" =
    tabParam === "pending_review" || tabParam === "approved" || tabParam === "rejected"
      ? tabParam
      : "all";

  const { page, setPage, limit, resetPage, search, setSearch, debouncedSearch } = usePagination({
    initialPage: 1,
    initialLimit: 10,
  });

  const { data, isLoading, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ["review", "applications", activeTab, debouncedSearch, limit],
    queryFn: ({ pageParam = 0 }) =>
      reviewApi.list({
        status: activeTab === "all" ? undefined : activeTab,
        search: debouncedSearch || undefined,
        offset: pageParam as number,
        limit,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.limit;
      return nextOffset < lastPage.total ? nextOffset : undefined;
    },
    enabled: true,
  });

  const handleTabChange = useCallback(
    (tab: "all" | "pending_review" | "approved" | "rejected") => {
      router.setParams({ tab });
      resetPage();
    },
    [router, resetPage]
  );

  const total = data?.pages?.[0]?.total ?? 0;
  const pendingCount = data?.pages?.[0]?.counts?.pending_review ?? 0;
  const approvedCount = data?.pages?.[0]?.counts?.approved ?? 0;
  const rejectedCount = data?.pages?.[0]?.counts?.rejected ?? 0;

  const items = data?.pages?.flatMap((p) => p.items) ?? [];

  const columns: TableColumn<ApplicationSummary>[] = [
    {
      key: "company_name",
      header: t("portal.dashboard.table.companyName"),
      width: 280,
      renderCell: (item) => (
        <AppText weight="medium" className="text-supporting-text text-base" numberOfLines={1}>
          {item.company_name ?? "—"}
        </AppText>
      ),
    },
    {
      key: "contact",
      header: t("portal.dashboard.table.primaryContact"),
      width: 250,
      flex: 1,
      renderCell: (item) => (
        <View>
          <AppText weight="medium" className="text-supporting-text text-base" numberOfLines={1}>
            {item.primary_contact_name ?? "—"}
          </AppText>
          <AppText className="text-dark-text-20 text-sm" numberOfLines={1}>
            {item.primary_contact_email ?? item.vendor_email ?? ""}
          </AppText>
        </View>
      ),
    },
    {
      key: "primary_industry",
      header: t("portal.dashboard.table.primaryIndustry"),
      width: 180,
      renderCell: (item) => (
        <AppText weight="medium" className="text-supporting-text text-base" numberOfLines={1}>
          {item.primary_industry ?? "—"}
        </AppText>
      ),
    },
    {
      key: "status",
      header: t("portal.dashboard.table.status"),
      width: 180,
      renderCell: (item) => {
        const variantMap = {
          approved: "filled-success",
          pending_review: "filled-warning",
          rejected: "filled-error",
          invited: "accent-blue",
        } as const;
        const labelMap = {
          approved: "portal.dashboard.approved",
          pending_review: "portal.dashboard.pendingReview",
          rejected: "portal.dashboard.rejected",
          invited: "portal.dashboard.invited",
        } as const;

        return (
          <View className="items-start">
            <Badge variant={variantMap[item.status]} label={labelMap[item.status]} />
          </View>
        );
      },
    },
    {
      key: "last_activity",
      header: t("portal.dashboard.table.lastActivity"),
      width: 240,
      renderCell: (item) => {
        const ts = item.last_activity ?? item.submitted_at;
        if (!ts) return <AppText className="text-supporting-text text-base">{"—"}</AppText>;
        const { date, time } = formatSplitDateTime(ts, locale);
        return (
          <>
            <AppText weight="medium" className="text-supporting-text text-base" numberOfLines={1}>
              {formatRelativeTime(ts, locale, t)}
            </AppText>
            <View className="flex-row items-center gap-1 mt-0.5">
              <AppText className="text-dark-text-20 text-sm">{date}</AppText>
              <View className="w-1 h-1 rounded-full bg-stroke-30" />
              <AppText className="text-dark-text-20 text-sm">{time}</AppText>
            </View>
          </>
        );
      },
    },
    {
      key: "actions",
      header: "",
      width: 140,
      renderCell: (item) => (
        <View className="items-end py-2">
          <Button
            label="common.view"
            variant="secondary"
            onPress={() => {
              router.push(`/(staff)/applications/${item.id}`);
            }}
            className="py-2 h-[42px] border-stroke-30 rounded"
            labelClassName="hover:text-primary text-supporting-text"
          />
        </View>
      ),
    },
  ];

  return (
    <View className="flex-1 bg-stroke-10">
      <ScrollView
        className="flex-1 bg-white"
        testID="staff-applications"
        contentContainerStyle={{ flexGrow: 1, paddingTop: top }}
      >
        {/* ── Page header ─────────────────────────────────────────── */}
        <View className="items-start px-page-x tablet:px-20 pt-10 mb-10">
          <AppText
            weight="bold"
            className="text-[29px] leading-[31.9px] tracking-[-0.29px] text-dark-text-50"
          >
            {t("review.dashboard.title")}
          </AppText>
          <AppText className="text-base text-supporting-text mt-1">
            {t("staff.applications.subtitle")}
          </AppText>

          {/* ── Stat cards ──────────────────────────────────────── */}
          <View className="items-start w-full flex-col tablet:flex-row tablet:flex-wrap tablet:max-w-4xl gap-4 mt-6">
            <StatCard
              icon={<Icon as={Feather} name="file-text" size={18} className="text-blue-50" />}
              iconBgClass="bg-blue-5"
              value={pendingCount}
              title={t("staff.applications.stats.new")}
              description={t("staff.applications.stats.newDesc")}
              className="w-full"
            />
            <StatCard
              icon={<Icon as={Feather} name="check-circle" size={18} className="text-green-50" />}
              iconBgClass="bg-green-5"
              value={approvedCount}
              title={t("staff.applications.stats.approved")}
              description={t("staff.applications.stats.approvedDesc")}
              className="w-full"
            />
            <StatCard
              icon={<Icon as={Feather} name="x-circle" size={18} className="text-error" />}
              iconBgClass="bg-error-light"
              value={rejectedCount}
              title={t("staff.applications.stats.rejected")}
              description={t("staff.applications.stats.rejectedDesc")}
              className="w-full"
            />
          </View>
        </View>

        {/* ── Table card ────────────────────────────────────────── */}
        <View className="w-full flex-col mb-10">
          <TabBarNavigation<"all" | "pending_review" | "approved" | "rejected">
            tabs={[
              { key: "all", label: t("staff.applications.tabs.all"), count: total },
              {
                key: "pending_review",
                label: t("staff.applications.tabs.pending"),
                count: pendingCount,
              },
              {
                key: "approved",
                label: t("staff.applications.tabs.approved"),
                count: approvedCount,
              },
              {
                key: "rejected",
                label: t("staff.applications.tabs.rejected"),
                count: rejectedCount,
              },
            ]}
            activeTab={activeTab}
            onTabPress={handleTabChange}
            contentContainerClassName="px-page-x tablet:px-20"
          />

          {/* Toolbar: Search */}
          <View className="px-page-x tablet:px-20">
            <View className="gap-3 flex-wrap flex-row tablet:items-center py-4">
              <SearchInput
                containerClassName="max-w-sm w-full bg-stroke-10 rounded-md mb-0"
                value={search}
                onChangeText={setSearch}
                placeholder="common.search"
              />
            </View>

            <View className="pb-6">
              <Table
                columns={columns}
                data={items}
                keyExtractor={(item) => item.id}
                minWidth={1000}
                limit={limit}
                totalCount={total}
                page={page}
                onPageChange={(p) => {
                  const nextOffset = (p - 1) * limit;
                  setPage(p);
                  if (
                    nextOffset >= (data?.pages?.flatMap((pg) => pg.items).length ?? 0) &&
                    hasNextPage
                  ) {
                    fetchNextPage();
                  }
                }}
                onRowPress={(item) => router.push(`/(staff)/applications/${item.id}`)}
                emptyDescription="review.dashboard.empty"
                isLoading={isLoading}
              />
            </View>
          </View>
        </View>
        <PageFooter />
      </ScrollView>
    </View>
  );
}
